import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Plus, ShoppingBag, MessageCircle, Wallet, TrendingUp, Trash2, ShieldCheck, AlertCircle, BadgeCheck, Crown, ArrowRight, Bell, MailCheck, Gift, Copy, Package, Save, Loader2, Trophy } from "lucide-react";
import { toast } from "sonner";

// Inline WhatsApp glyph reused on the profile-settings card.
const WhatsAppIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.768.967-.94 1.165-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479c0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.695.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zm-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const StatCard = ({ icon: Icon, label, value, hint }) => (
  <Card className="p-6 bg-white border-black/10 rounded-lg">
    <div className="flex items-start justify-between">
      <div>
        <div className="label-overline text-muted-foreground">{label}</div>
        <div className="font-display text-3xl mt-2">{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      </div>
      <Icon className="w-5 h-5 text-[#C05A3C]"/>
    </div>
  </Card>
);

const Dashboard = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [myListings, setMyListings] = useState([]);
  const [convs, setConvs] = useState([]);
  const [savedSearches, setSavedSearches] = useState([]);
  const [referral, setReferral] = useState(null);
  const [refLink, setRefLink] = useState(null);
  const [pulseSubscribed, setPulseSubscribed] = useState(true);
  const [whatsapp, setWhatsapp] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [leaderboardRank, setLeaderboardRank] = useState(null);

  // Hydrate WhatsApp input from the logged-in user once they're loaded.
  useEffect(() => { if (user) setWhatsapp(user.whatsapp || ""); }, [user]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const { data } = await api.patch("/me/profile", { whatsapp });
      setUser(data.user);
      toast.success(whatsapp ? "WhatsApp saved — buyers can now message you" : "WhatsApp removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save profile");
    } finally { setSavingProfile(false); }
  };

  // Honour redirects from the email-tap endpoint
  useEffect(() => {
    if (params.get("saved_search") === "created") {
      toast.success("Saved — we'll email you when new matches go live");
      params.delete("saved_search"); setParams(params, { replace: true });
    } else if (params.get("saved_search_error")) {
      toast.error("That save-search link expired or is invalid");
      params.delete("saved_search_error"); setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = () => {
    if (!user) return;
    api.get("/listings", { params: { owner_id: user.user_id, approval: "all", limit: 100 } }).then((r) => setMyListings(r.data));
    api.get("/conversations").then((r) => setConvs(r.data));
    api.get("/saved-searches").then((r) => setSavedSearches(r.data)).catch(() => {});
    api.get("/me/referral-summary").then((r) => setReferral(r.data)).catch(() => {});
    api.get("/me/referral-link").then((r) => setRefLink(r.data)).catch(() => {});
    api.get("/me/pulse/preferences").then((r) => setPulseSubscribed(r.data.pulse_subscribed)).catch(() => {});
    api.get("/me/referral-leaderboard-position").then((r) => setLeaderboardRank(r.data)).catch(() => setLeaderboardRank(null));
  };

  useEffect(load, [user]);

  const remove = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    try {
      await api.delete(`/listings/${id}`);
      toast.success("Listing deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const removeSavedSearch = async (id) => {
    try {
      await api.delete(`/saved-searches/${id}`);
      toast.success("Saved search removed");
      setSavedSearches((s) => s.filter((x) => x.search_id !== id));
    } catch {
      toast.error("Could not remove");
    }
  };

  const sendTestAlert = async () => {
    try {
      const { data } = await api.post("/saved-searches/test-email");
      toast.success(`Test alert sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send test email");
    }
  };

  const togglePulse = async () => {
    const next = !pulseSubscribed;
    try {
      await api.post("/me/pulse/preferences", { subscribed: next });
      setPulseSubscribed(next);
      toast.success(next ? "You'll get the weekly Pulse on Wednesdays" : "Unsubscribed from AfriMarket Pulse");
    } catch {
      toast.error("Could not update preference");
    }
  };

  const previewPulse = () => {
    const token = localStorage.getItem("token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/me/pulse/preview`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.text())
      .then((html) => {
        const win = window.open("", "_blank");
        win.document.write(html);
        win.document.close();
      })
      .catch(() => toast.error("Could not load pulse preview"));
  };

  const searchLink = (s) => {
    const p = new URLSearchParams();
    ["type", "category", "country", "region", "q", "sort"].forEach((k) => { if (s[k]) p.set(k, s[k]); });
    return `/browse?${p.toString()}`;
  };

  if (!user) return null;

  return (
    <div className="container-narrow py-10">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="label-overline text-[#C05A3C] mb-1">Welcome back</div>
          <h1 className="font-display text-4xl flex items-center gap-2">
            Hello, {user.name}
            {user.verified_badge && <BadgeCheck className="w-7 h-7 text-[#C05A3C]" title="Verified"/>}
            {user.is_pro && <Crown className="w-6 h-6 text-[#D9923B]" title="Pro member"/>}
          </h1>
        </div>
        <Button onClick={() => navigate(user.verification_status === "approved" ? "/create" : "/verify")} data-testid="dashboard-create-btn" className="bg-[#C05A3C] hover:bg-[#D46B4E] rounded-md">
          <Plus className="w-4 h-4 mr-1"/> {user.verification_status === "approved" ? "New listing" : "Get verified"}
        </Button>
      </div>

      {/* Verification banner */}
      {user.verification_status !== "approved" && (
        <div className={`mb-6 p-5 rounded-lg border flex items-start gap-4 ${user.verification_status === "pending" ? "bg-[#D9923B]/10 border-[#D9923B]/30" : user.verification_status === "rejected" ? "bg-[#B84A28]/10 border-[#B84A28]/30" : "bg-[#C05A3C]/10 border-[#C05A3C]/30"}`} data-testid="verify-banner">
          <ShieldCheck className="w-5 h-5 mt-0.5 shrink-0 text-[#C05A3C]"/>
          <div className="flex-1">
            <div className="font-semibold">
              {user.verification_status === "pending" ? "Verification under review"
                : user.verification_status === "rejected" ? "Verification was rejected"
                : "Get verified to start selling"}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {user.verification_status === "pending"
                ? "Our team is reviewing your documents. You'll be able to publish listings once approved."
                : "Submit your government ID + proof of address. Most reviews complete within 24-48 hours."}
            </div>
          </div>
          <Button onClick={() => navigate("/verify")} variant="outline" data-testid="verify-banner-cta">
            {user.verification_status === "pending" ? "View status" : "Start"}
          </Button>
        </div>
      )}

      {/* Pro card — shown to approved sellers */}
      {user.verification_status === "approved" && (
        user.is_pro ? (
          <div className="mb-6 p-5 rounded-lg bg-[#1A2024] text-white flex items-center gap-4 relative overflow-hidden" data-testid="pro-card-active">
            <div className="absolute inset-0 ankara-stripe opacity-20" aria-hidden/>
            <Crown className="w-7 h-7 text-[#D9923B] relative shrink-0"/>
            <div className="flex-1 relative">
              <div className="font-semibold flex items-center gap-2">You&apos;re a Pro member <Badge className="bg-[#D9923B] text-[#1A2024]">PRO</Badge></div>
              <div className="text-sm text-white/70 mt-0.5">Enjoying 5% flat fees and priority placement. Listings auto-flagged with the ✦ Pro mark.</div>
            </div>
            <Button variant="outline" onClick={() => navigate("/pro")} className="border-white/30 text-white hover:bg-white hover:text-[#1A2024] relative" data-testid="manage-pro">Manage Pro</Button>
          </div>
        ) : (
          <div className="mb-6 p-5 rounded-lg bg-gradient-to-br from-[#D9923B]/10 to-[#C05A3C]/10 border border-[#D9923B]/30 flex items-center gap-4" data-testid="pro-upsell">
            <Crown className="w-7 h-7 text-[#D9923B] shrink-0"/>
            <div className="flex-1">
              <div className="font-semibold">Upgrade to AfriMarket Pro — 5% flat fee + verified badge</div>
              <div className="text-sm text-muted-foreground mt-0.5">Save 3-5% on every sale and stand out with a Pro mark on every listing. Founders pay £4.99 / 30 days for life.</div>
            </div>
            <Button onClick={() => navigate("/pro")} className="bg-[#D9923B] hover:bg-[#D9923B]/90 text-[#1A2024] font-semibold" data-testid="goto-pro">Upgrade <ArrowRight className="w-4 h-4 ml-1"/></Button>
          </div>
        )
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard icon={ShoppingBag} label="Active listings" value={myListings.length} hint="Products & services"/>
        <StatCard icon={MessageCircle} label="Conversations" value={convs.length} hint="Inbox messages"/>
        <StatCard icon={Wallet} label="Currency" value={user.country || "—"} hint="Your country"/>
        <StatCard icon={TrendingUp} label="Member since" value={new Date(user.created_at).toLocaleDateString("en", { month: "short", year: "numeric" })}/>
      </div>

      {/* Quick links + WhatsApp setting */}
      <div className="grid md:grid-cols-4 gap-4 mb-10">
        <Link to="/dashboard/purchases" data-testid="quicklink-purchases" className="bg-white border border-black/10 rounded-lg p-5 flex items-center gap-3 hover:border-[#C05A3C] transition-colors group">
          <div className="shrink-0 w-11 h-11 rounded-lg bg-[#C05A3C]/10 grid place-items-center group-hover:bg-[#C05A3C] group-hover:text-white transition-colors">
            <Package className="w-5 h-5"/>
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm">My Orders</div>
            <div className="text-xs text-muted-foreground mt-0.5">Track every purchase</div>
          </div>
        </Link>
        <Link to="/dashboard/sales" data-testid="quicklink-sales" className="bg-white border border-black/10 rounded-lg p-5 flex items-center gap-3 hover:border-[#C05A3C] transition-colors group">
          <div className="shrink-0 w-11 h-11 rounded-lg bg-[#C05A3C]/10 grid place-items-center group-hover:bg-[#C05A3C] group-hover:text-white transition-colors">
            <ShoppingBag className="w-5 h-5"/>
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm">My Sales</div>
            <div className="text-xs text-muted-foreground mt-0.5">Manage fulfilment</div>
          </div>
        </Link>
        <Link to="/dashboard/messages" data-testid="quicklink-messages" className="bg-white border border-black/10 rounded-lg p-5 flex items-center gap-3 hover:border-[#C05A3C] transition-colors group">
          <div className="shrink-0 w-11 h-11 rounded-lg bg-[#2A3D36]/10 grid place-items-center group-hover:bg-[#2A3D36] group-hover:text-white transition-colors">
            <MessageCircle className="w-5 h-5"/>
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm">Messages</div>
            <div className="text-xs text-muted-foreground mt-0.5">{convs.length} conversation{convs.length === 1 ? "" : "s"}</div>
          </div>
        </Link>
        <Link to="/dashboard/payouts" data-testid="quicklink-payouts" className="bg-white border border-black/10 rounded-lg p-5 flex items-center gap-3 hover:border-[#C05A3C] transition-colors group">
          <div className="shrink-0 w-11 h-11 rounded-lg bg-[#D9923B]/15 grid place-items-center group-hover:bg-[#D9923B] group-hover:text-white transition-colors">
            <Wallet className="w-5 h-5"/>
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm">Payouts</div>
            <div className="text-xs text-muted-foreground mt-0.5">Stripe Connect</div>
          </div>
        </Link>
      </div>

      {/* Referral leaderboard rank strip — only shows when the user has at
          least placed (any rank, any earnings) this month. */}
      {leaderboardRank && (leaderboardRank.me?.position || (leaderboardRank.me?.total || 0) > 0) && (
        <Link
          to="/leaderboard"
          data-testid="dashboard-rank-strip"
          className="block mb-4 p-4 rounded-2xl bg-gradient-to-r from-[#1A2024] to-[#2A3035] text-white hover:from-[#22282C] hover:to-[#32383D] transition-all"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#D9923B] flex items-center justify-center text-lg font-display shrink-0">
                {leaderboardRank.me.position ? `#${leaderboardRank.me.position}` : <Trophy className="w-5 h-5"/>}
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-white/60 mb-0.5">
                  Referral leaderboard · {leaderboardRank.month}
                </div>
                <div className="font-semibold text-sm">
                  {leaderboardRank.me.in_prize_zone ? (
                    <>You&apos;re in the prize zone — £{leaderboardRank.prize_gbp} on the line 🏆</>
                  ) : leaderboardRank.gap_to_prize_zone !== null && leaderboardRank.gap_to_prize_zone > 0 ? (
                    <>Just £{leaderboardRank.gap_to_prize_zone.toFixed(2)} from the top {leaderboardRank.prize_top_n}</>
                  ) : leaderboardRank.me.position ? (
                    <>You&rsquo;re #{leaderboardRank.me.position} this month · £{(leaderboardRank.me.total || 0).toFixed(2)} earned</>
                  ) : (
                    <>Refer a sale to enter the leaderboard</>
                  )}
                </div>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-white/60"/>
          </div>
        </Link>
      )}

      {/* Avatar upload — profile picture across the marketplace */}
      <Card className="p-5 bg-white border-black/10 mb-4" data-testid="avatar-settings-card">
        <div className="flex items-center gap-5 flex-wrap">
          <div className="w-16 h-16 rounded-full bg-[#F3EFE9] overflow-hidden flex items-center justify-center text-xl font-display shrink-0" data-testid="avatar-current">
            {user.picture
              ? <img src={user.picture.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${user.picture}` : user.picture} alt={user.name} className="w-full h-full object-cover"/>
              : (user.name || "?").slice(0, 1)}
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="font-semibold">Profile picture</div>
            <div className="text-xs text-muted-foreground mt-0.5">JPG or PNG, max 5MB. Shows next to every listing you post.</div>
            <input
              id="avatar-input"
              type="file"
              accept="image/*"
              hidden
              data-testid="avatar-file-input"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                if (!f.type.startsWith("image/")) { toast.error("Pick an image"); return; }
                if (f.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
                try {
                  const fd = new FormData();
                  fd.append("avatar", f);
                  const { data } = await api.post("/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
                  setUser(data.user);
                  toast.success("Profile picture updated");
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Upload failed");
                }
                e.target.value = "";
              }}
            />
            <Button
              data-testid="avatar-upload-btn"
              onClick={() => document.getElementById("avatar-input")?.click()}
              className="mt-3 bg-[#C05A3C] hover:bg-[#A24A30] text-white rounded-md h-9 px-4 text-sm"
            >
              <Save className="w-4 h-4 mr-1.5"/> {user.picture ? "Change photo" : "Upload photo"}
            </Button>
          </div>
        </div>
      </Card>

      {/* WhatsApp settings — lets buyers reach the seller directly */}
      <Card className="p-5 bg-white border-black/10 mb-10" data-testid="whatsapp-settings-card">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="shrink-0 w-11 h-11 rounded-lg bg-[#25D366]/15 grid place-items-center">
            <WhatsAppIcon className="w-5 h-5 text-[#25D366]" />
          </div>
          <div className="flex-1 min-w-[280px]">
            <div className="font-semibold">Chat on WhatsApp</div>
            <div className="text-xs text-muted-foreground mt-0.5 max-w-xl">
              Add your WhatsApp number to let buyers reach you instantly from any of your listings. Include the country code, e.g. <code className="px-1 py-0.5 bg-[#F3EFE9] rounded">+2348012345678</code> or <code className="px-1 py-0.5 bg-[#F3EFE9] rounded">+447911123456</code>.
            </div>
            <div className="mt-3 flex flex-col sm:flex-row gap-2 max-w-md">
              <input
                data-testid="whatsapp-input"
                type="tel"
                inputMode="tel"
                placeholder="+234 801 234 5678"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                className="flex-1 h-10 px-3 rounded-md border border-black/15 bg-white text-sm focus:border-[#25D366] focus:outline-none focus:ring-2 focus:ring-[#25D366]/30"
              />
              <Button
                data-testid="whatsapp-save-btn"
                onClick={saveProfile}
                disabled={savingProfile || whatsapp === (user.whatsapp || "")}
                className="bg-[#25D366] hover:bg-[#1FB155] text-white rounded-md h-10 px-4"
              >
                {savingProfile ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Save className="w-4 h-4 mr-1.5"/>Save</>}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Share earnings */}
      {refLink && (
        <Card className="p-6 bg-[#1A2024] text-white border-transparent mb-8 relative overflow-hidden" data-testid="referral-card">
          <div className="absolute inset-0 ankara-stripes opacity-10 pointer-events-none"/>
          <div className="relative grid md:grid-cols-3 gap-6 items-center">
            <div className="md:col-span-2">
              <div className="label-overline text-[#D9923B] mb-2 inline-flex items-center gap-2"><Gift className="w-3.5 h-3.5"/> Share earnings</div>
              <div className="font-display text-2xl mb-1">
                Earn {refLink.rate_pct}% on every sale you refer.
              </div>
              <p className="text-sm text-white/75 leading-relaxed mb-4">
                Share any listing — when someone buys via your link, we credit you {refLink.rate_pct}% of the platform fee. Pays out the moment the buyer pays.
              </p>
              <div className="flex gap-2 items-center bg-white/10 rounded-md px-3 py-2 max-w-md">
                <code className="text-xs flex-1 truncate" data-testid="referral-link">{refLink.url}</code>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(refLink.url);
                      toast.success("Link copied");
                    } catch {
                      toast.error("Couldn't copy");
                    }
                  }}
                  data-testid="copy-referral-link"
                  className="text-[#D9923B] hover:text-[#D9923B]/80 p-1"
                  aria-label="Copy referral link"
                >
                  <Copy className="w-4 h-4"/>
                </button>
              </div>
            </div>
            <div className="text-right md:border-l md:border-white/10 md:pl-6">
              <div className="label-overline text-white/60 mb-1">Earned so far</div>
              <div className="font-display text-4xl text-[#D9923B] leading-none" data-testid="referral-total">
                {referral && Object.keys(referral.totals_by_currency || {}).length > 0
                  ? Object.entries(referral.totals_by_currency).map(([c, v]) => `${c} ${v.toFixed(2)}`).join(" · ")
                  : "—"}
              </div>
              <div className="text-xs text-white/60 mt-2">{referral?.transaction_count || 0} {referral?.transaction_count === 1 ? "referred sale" : "referred sales"}</div>
            </div>
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl">My listings</h2>
            <Link to="/dashboard/messages" className="text-sm text-muted-foreground hover:text-[#C05A3C]">Open messages →</Link>
          </div>
          {myListings.length === 0 ? (
            <Card className="p-10 text-center bg-white border-black/10">
              <ShoppingBag className="w-10 h-10 mx-auto text-muted-foreground mb-3"/>
              <h3 className="font-display text-2xl">No listings yet</h3>
              <p className="text-muted-foreground mt-1">Create your first listing to start selling.</p>
              <Button onClick={() => navigate("/create")} className="mt-5 bg-[#C05A3C] hover:bg-[#D46B4E] rounded-md" data-testid="empty-create-btn">
                <Plus className="w-4 h-4 mr-1"/> Create listing
              </Button>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="my-listings">
              {myListings.map((l) => (
                <div key={l.listing_id} className="flex items-center gap-4 p-4 bg-white border border-black/10 rounded-lg">
                  <Link to={`/listing/${l.listing_id}`} className="shrink-0 w-20 h-20 bg-[#F3EFE9] rounded-md overflow-hidden">
                    {l.images?.[0] && <img src={l.images[0]} alt="" className="w-full h-full object-cover"/>}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className="bg-[#F3EFE9] text-[#2A3D36] capitalize">{l.type}</Badge>
                      <Badge variant="outline" className={`text-xs ${l.approval_status === "approved" ? "border-[#4A5D23] text-[#4A5D23]" : l.approval_status === "rejected" ? "border-[#B84A28] text-[#B84A28]" : "border-[#D9923B] text-[#D9923B]"}`}>
                        {l.approval_status === "pending" ? "Pending review" : l.approval_status === "rejected" ? "Rejected" : "Live"}
                      </Badge>
                      {l.status === "removed" && <Badge className="bg-[#B84A28]/15 text-[#B84A28]">Removed by admin</Badge>}
                    </div>
                    <Link to={`/listing/${l.listing_id}`} className="font-display text-lg hover:text-[#C05A3C] truncate block">{l.title}</Link>
                    <div className="text-xs text-muted-foreground">{l.city}, {l.country} · {l.category}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold font-sans-ui">{l.currency} {l.price}</div>
                    <button onClick={() => remove(l.listing_id)} className="text-xs text-muted-foreground hover:text-red-600 mt-2 inline-flex items-center" data-testid={`delete-${l.listing_id}`}>
                      <Trash2 className="w-3 h-3 mr-1"/> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl flex items-center gap-2"><Bell className="w-5 h-5 text-[#C05A3C]"/> Saved searches</h2>
              <button onClick={sendTestAlert} className="text-xs text-muted-foreground hover:text-[#C05A3C] inline-flex items-center gap-1" data-testid="saved-test-email-btn" title="Send a sample alert email to confirm Resend is wired up">
                <MailCheck className="w-3 h-3"/> Test alert
              </button>
            </div>
            {savedSearches.length === 0 ? (
              <Card className="p-5 bg-white border-black/10 text-center" data-testid="saved-empty">
                <Bell className="w-7 h-7 mx-auto text-muted-foreground mb-2"/>
                <p className="text-sm text-muted-foreground">No saved searches yet. Save a Browse filter and get emailed when matches go live.</p>
                <Button onClick={() => navigate("/browse")} variant="outline" className="mt-4" data-testid="saved-empty-cta">Browse listings</Button>
              </Card>
            ) : (
              <div className="space-y-2" data-testid="saved-list">
                {savedSearches.map((s) => (
                  <div key={s.search_id} className="p-3 bg-white border border-black/10 rounded-lg flex items-center gap-3" data-testid={`saved-${s.search_id}`}>
                    <div className="flex-1 min-w-0">
                      <Link to={searchLink(s)} className="block">
                        <div className="font-semibold text-sm truncate">{s.name}</div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {[s.type, s.category, s.region || s.country, s.q && `"${s.q}"`].filter(Boolean).join(" · ") || "All listings"}
                        </div>
                      </Link>
                    </div>
                    <button onClick={() => removeSavedSearch(s.search_id)} className="text-muted-foreground hover:text-red-600" data-testid={`saved-delete-${s.search_id}`} title="Remove">
                      <Trash2 className="w-4 h-4"/>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Card className="p-5 bg-gradient-to-br from-[#C05A3C] to-[#1A2024] text-white border-0" data-testid="pulse-card">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="label-overline text-[#F2C94C]">AfriMarket Pulse</div>
                <div className="font-display text-xl mt-1">Weekly trending picks</div>
                <div className="text-sm text-white/80 mt-2">Every Wednesday — top listings, hot categories, new sellers near you.</div>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <Button onClick={togglePulse} variant={pulseSubscribed ? "secondary" : "default"} size="sm" data-testid="pulse-toggle-btn" className={pulseSubscribed ? "bg-white text-[#1A2024] hover:bg-white/90" : "bg-[#F2C94C] text-[#1A2024] hover:bg-[#F2C94C]/90"}>
                {pulseSubscribed ? "Subscribed ✓" : "Subscribe"}
              </Button>
              <Button onClick={previewPulse} variant="ghost" size="sm" data-testid="pulse-preview-btn" className="text-white hover:bg-white/10 hover:text-white">
                Preview email →
              </Button>
            </div>
          </Card>

          <div>
            <h2 className="font-display text-2xl mb-4">Recent messages</h2>
          {convs.length === 0 ? (
            <Card className="p-6 bg-white border-black/10 text-center">
              <MessageCircle className="w-8 h-8 mx-auto text-muted-foreground mb-2"/>
              <p className="text-sm text-muted-foreground">No messages yet</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {convs.slice(0, 5).map((c) => (
                <Link key={c.conversation_id} to={`/dashboard/messages?c=${c.conversation_id}`} className="block p-4 bg-white border border-black/10 rounded-lg hover:border-[#C05A3C]" data-testid={`conv-${c.conversation_id}`}>
                  <div className="text-sm font-semibold truncate">{c.other_user?.name || "User"}</div>
                  <div className="text-xs text-muted-foreground truncate">{c.listing_title}</div>
                  <div className="text-xs text-muted-foreground truncate mt-1">{c.last_message || "Say hello"}</div>
                </Link>
              ))}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
